import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import {
  createChartOfAccount,
  type ChartAccountClassKey,
  type ChartAccountLevel,
  type ChartOfAccountNode,
} from "@/lib/dataBank/chartOfAccountsStore";
import { isAxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

export type CreateChartContext = {
  level: ChartAccountLevel;
  /** Preselected L1 parent when adding L2 */
  parentTypeId?: number | null;
  parentTypeName?: string | null;
  parentTypeCode?: string | null;
  /** Preselected L2 parent when adding L3 */
  parentDetailTypeId?: number | null;
  parentDetailName?: string | null;
  parentDetailCode?: string | null;
};

type CreateChartOfAccountDialogProps = {
  open: boolean;
  context: CreateChartContext | null;
  tree: ChartOfAccountNode[];
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
};

const CLASS_OPTIONS: { value: ChartAccountClassKey; label: string }[] = [
  { value: "assets", label: "Asset" },
  { value: "liabilities", label: "Liability" },
  { value: "income", label: "Income" },
  { value: "expense", label: "Expense" },
  { value: "cos", label: "Cost of Sales" },
];

function levelTitle(level: ChartAccountLevel): string {
  if (level === 1) return "Add account type (Level 1)";
  if (level === 2) return "Add account group (Level 2)";
  return "Add account (Level 3)";
}

function levelHint(level: ChartAccountLevel): string {
  if (level === 1) return "Creates a top-level account type (e.g. Current Liabilities).";
  if (level === 2) return "Creates a detail group under the selected account type (e.g. Investors Capital).";
  return "Creates a posting account under the selected group (e.g. 220602 Investors Capital).";
}

export function CreateChartOfAccountDialog({
  open,
  context,
  tree,
  onOpenChange,
  onCreated,
}: CreateChartOfAccountDialogProps) {
  const level = context?.level ?? 3;
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [accountClass, setAccountClass] = useState<ChartAccountClassKey>("liabilities");
  const [parentTypeId, setParentTypeId] = useState<string>("");
  const [parentDetailId, setParentDetailId] = useState<string>("");
  const [saving, setSaving] = useState(false);

  const typeOptions = useMemo(
    () =>
      tree
        .filter((n) => n.level === 1)
        .map((n) => ({
          id: n.id.replace(/^type-/, ""),
          name: n.name,
          code: n.code,
        })),
    [tree],
  );

  const detailOptions = useMemo(() => {
    const typeId = parentTypeId || (context?.parentTypeId != null ? String(context.parentTypeId) : "");
    const typeNode = tree.find((n) => n.id === `type-${typeId}`);
    const groups = typeNode
      ? typeNode.children
      : tree.flatMap((n) => n.children);
    return groups
      .filter((n) => n.level === 2)
      .map((n) => ({
        id: n.id.replace(/^detail-/, ""),
        name: n.name,
        code: n.code,
        typeId: tree.find((t) => t.children.some((c) => c.id === n.id))?.id.replace(/^type-/, "") ?? "",
      }));
  }, [tree, parentTypeId, context?.parentTypeId]);

  useEffect(() => {
    if (!open || !context) return;
    setName("");
    setCode("");
    setAccountClass("liabilities");
    setParentTypeId(context.parentTypeId != null ? String(context.parentTypeId) : "");
    setParentDetailId(context.parentDetailTypeId != null ? String(context.parentDetailTypeId) : "");
  }, [open, context]);

  const handleSave = async () => {
    if (!context) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("Name is required.");
      return;
    }

    setSaving(true);
    try {
      await createChartOfAccount({
        level: context.level,
        name: trimmedName,
        code: code.trim() || null,
        account_class: context.level === 1 ? accountClass : null,
        parent_type_id:
          context.level === 2
            ? Number(parentTypeId || context.parentTypeId || 0) || null
            : null,
        parent_detail_type_id:
          context.level === 3
            ? Number(parentDetailId || context.parentDetailTypeId || 0) || null
            : null,
      });
      toast.success("Account created.");
      onOpenChange(false);
      onCreated();
    } catch (error) {
      const message = isAxiosError(error)
        ? (error.response?.data as { message?: string } | undefined)?.message
        : null;
      toast.error(message || "Could not create account.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{levelTitle(level)}</DialogTitle>
          <DialogDescription>{levelHint(level)}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {level === 1 ? (
            <div className="space-y-1.5">
              <Label>Account class</Label>
              <Select value={accountClass} onValueChange={(v) => setAccountClass(v as ChartAccountClassKey)}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Select class" />
                </SelectTrigger>
                <SelectContent>
                  {CLASS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          {level === 2 ? (
            <div className="space-y-1.5">
              <Label>Parent account type (Level 1)</Label>
              {context?.parentTypeName ? (
                <div className="rounded-md border border-border/50 bg-muted/30 px-3 py-2 text-sm">
                  {context.parentTypeName}
                  {context.parentTypeCode ? (
                    <span className="ml-2 font-mono text-xs text-muted-foreground">{context.parentTypeCode}</span>
                  ) : null}
                </div>
              ) : (
                <Select value={parentTypeId} onValueChange={setParentTypeId}>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="Select account type" />
                  </SelectTrigger>
                  <SelectContent>
                    {typeOptions.map((opt) => (
                      <SelectItem key={opt.id} value={opt.id}>
                        {opt.name} ({opt.code || "—"})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          ) : null}

          {level === 3 ? (
            <div className="space-y-1.5">
              <Label>Parent account group (Level 2)</Label>
              {context?.parentDetailName ? (
                <div className="rounded-md border border-border/50 bg-muted/30 px-3 py-2 text-sm">
                  {context.parentDetailName}
                  {context.parentDetailCode ? (
                    <span className="ml-2 font-mono text-xs text-muted-foreground">{context.parentDetailCode}</span>
                  ) : null}
                </div>
              ) : (
                <Select value={parentDetailId} onValueChange={setParentDetailId}>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="Select account group" />
                  </SelectTrigger>
                  <SelectContent>
                    {detailOptions.map((opt) => (
                      <SelectItem key={opt.id} value={opt.id}>
                        {opt.name} ({opt.code || "—"})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          ) : null}

          <div className="space-y-1.5">
            <Label>{level === 3 ? "Account name" : "Name"}</Label>
            <Input
              className="h-10"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={level === 3 ? "e.g. Investors Capital" : "e.g. Current Liabilities"}
            />
          </div>

          <div className="space-y-1.5">
            <Label>
              Code {level === 3 ? <span className="text-muted-foreground font-normal">(optional — auto if blank)</span> : null}
            </Label>
            <Input
              className="h-10 font-mono"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, ""))}
              placeholder={level === 1 ? "e.g. 2200" : level === 2 ? "e.g. 2206" : "e.g. 220602"}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleSave()} disabled={saving}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
